import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SI_COPYRIGHT_DETAILS } from '@siemens/element-ng/copyright-notice';
import { provideNgxTranslateForElement } from '@siemens/element-translate-ng/ngx-translate';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { NgxMonacoEditorConfig, provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { KeycloakService } from 'keycloak-angular';
import { routes } from './app.routes';
import { initializeKeycloak } from './init/keycloak-init.factory';
import { authInterceptorFn } from './interceptors/auth.interceptor';
import { customTheme } from './custom-theme/editor-custom-theme';
import { AppConfigService } from './services/app-config.service';

// Small typed definition for window.monaco to avoid casting to `any`
declare global {
  interface Window {
    monaco?: {
      editor?: {
        defineTheme?: (name: string, theme: unknown) => void;
        setTheme?: (name: string) => void;
      };
    };
  }
}

export const createTranslateLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, 'assets/i18n/', '.json');


const monacoConfig: NgxMonacoEditorConfig = {
  onMonacoLoad: () => {
    const monaco = window.monaco;
    if (monaco?.editor) {
      monaco.editor.defineTheme?.('customTheme', customTheme);
      monaco.editor.setTheme?.('customTheme');
    }
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptorFn])
    ),
    provideRouter(routes, withHashLocation()),
    provideNgxTranslateForElement(),
    provideMonacoEditor(monacoConfig),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      })
    ),
    {
      provide: SI_COPYRIGHT_DETAILS,
      useValue: {
        startYear: new Date().getFullYear()
      }
    },
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js')
    }),
    
    // Runtime config + Keycloak: config is loaded inside the keycloak initializer
    AppConfigService,
    KeycloakService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService, AppConfigService]
    }
  ]
}
